import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
} from '@angular/core';

const DEFAULT_ACCEPT = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_BYTES = 20 * 1024 * 1024;

export type DropZoneRejectedFile = {
  file: File;
  reason: string;
};

@Component({
  selector: 'app-drop-zone',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './drop-zone.component.html',
  styleUrls: ['./drop-zone.component.scss'],
})
export class DropZoneComponent {
  @Input() accept: string[] = DEFAULT_ACCEPT;
  @Input() maxBytes = MAX_BYTES;
  @Input() multiple = true;
  @Input() disabled = false;
  @Input() label = 'Attach file';
  /** Optional override for the drag hint above the button. */
  @Input() hint = 'Drag files here or use the button below';
  /** Optional secondary line under the button (e.g. accepted types). */
  @Input() acceptHint = '';
  /** Show upload icon above the hint (Figma Add job DropZone). */
  @Input() showIcon = false;
  /** Optional chip shown on the drop zone (e.g. staff visibility). */
  @Input() chip = '';
  @Input() chipTone: 'staff' | 'office' | '' = '';
  /** When false, only drops on this zone count (avoids dual zones both catching page drops). */
  @Input() pageWideDrop = true;
  /** CSS selector — page-wide drops whose target matches are ignored (e.g. approval evidence zone). */
  @Input() pageWideIgnoreSelector = '';
  /** Show busy/spinner state while parent uploads. */
  @Input() loading = false;

  @Output() filesSelected = new EventEmitter<File[]>();
  /** Alias for `filesSelected` — parent handles upload to API. */
  @Output() uploads = new EventEmitter<File[]>();
  @Output() rejected = new EventEmitter<DropZoneRejectedFile[]>();

  @HostBinding('class.pp-drop-zone--dragging')
  dragging = false;

  @HostBinding('class.pp-drop-zone--disabled')
  get isDisabled() {
    return this.disabled || this.loading;
  }

  @HostBinding('class.pp-drop-zone--loading')
  get isLoading() {
    return this.loading;
  }

  private shouldIgnorePageWideTarget(event: DragEvent): boolean {
    if (!this.pageWideIgnoreSelector) return false;
    const target = event.target as Element | null;
    return !!target?.closest?.(this.pageWideIgnoreSelector);
  }

  @HostListener('document:dragenter', ['$event'])
  onDocumentDragEnter(event: DragEvent) {
    if (
      !this.pageWideDrop ||
      this.disabled ||
      this.loading ||
      this.shouldIgnorePageWideTarget(event) ||
      !event.dataTransfer?.types?.includes('Files')
    ) {
      return;
    }
    this.dragging = true;
    document.body.classList.add('pp-drop-zone-overlay');
  }

  @HostListener('document:dragover', ['$event'])
  onDocumentDragOver(event: DragEvent) {
    if (!this.pageWideDrop || !this.dragging || this.disabled || this.loading) return;
    if (this.shouldIgnorePageWideTarget(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  @HostListener('document:dragleave', ['$event'])
  onDocumentDragLeave(event: DragEvent) {
    if (!this.pageWideDrop) return;
    if (event.relatedTarget == null) {
      this.clearDragOverlay();
    }
  }

  /** CR-26 / F1 — drop anywhere on the page attaches (same as HR overlay). */
  @HostListener('document:drop', ['$event'])
  onDocumentDrop(event: DragEvent) {
    if (
      !this.pageWideDrop ||
      !this.dragging ||
      this.disabled ||
      this.loading ||
      this.shouldIgnorePageWideTarget(event)
    ) {
      if (this.pageWideDrop) this.clearDragOverlay();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.clearDragOverlay();
    const list = event.dataTransfer?.files;
    if (list?.length) {
      this.handleFiles(Array.from(list));
    }
  }

  onDragOver(event: DragEvent) {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.clearDragOverlay();
    const list = event.dataTransfer?.files;
    if (list?.length) {
      this.handleFiles(Array.from(list));
    }
  }

  onFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const list = input.files;
    if (list?.length) {
      this.handleFiles(Array.from(list));
    }
    input.value = '';
  }

  private clearDragOverlay() {
    this.dragging = false;
    document.body.classList.remove('pp-drop-zone-overlay');
  }

  private handleFiles(files: File[]) {
    const accepted: File[] = [];
    const rejected: DropZoneRejectedFile[] = [];

    for (const file of files) {
      if (!this.accept.includes(file.type)) {
        rejected.push({
          file,
          reason: 'File type not allowed (PDF, JPG, PNG, DOCX).',
        });
        continue;
      }
      if (file.size > this.maxBytes) {
        rejected.push({ file, reason: 'File exceeds 20MB limit.' });
        continue;
      }
      accepted.push(file);
    }

    if (rejected.length) {
      this.rejected.emit(rejected);
    }
    if (accepted.length) {
      const batch = this.multiple ? accepted : [accepted[0]];
      this.filesSelected.emit(batch);
      this.uploads.emit(batch);
    }
  }
}
